import { MonthlyDatum } from './RestaurantOpeningsClosings.types';
import { getViewportGrid } from '@components/LineChart/LineChart.helpers';

export interface Scale {
    getY: (value: number) => number;
    ticks: number[];
}

export const getScale = (plotHeight: number, values: number[]): Scale => {
    const grid = getViewportGrid({
        chartValues: values.length > 0 ? values : [0],
        height: plotHeight,
    });
    return { getY: grid.getY, ticks: grid.yAxisValues };
};

export interface YearSeparator {
    index: number;
    year: number;
}

export const getYearSeparators = (data: MonthlyDatum[]): YearSeparator[] => {
    const separators: YearSeparator[] = [];
    for (let i = 1; i < data.length; i++) {
        const prevYear = data[i - 1].date.slice(0, 4);
        const currYear = data[i].date.slice(0, 4);
        if (currYear !== prevYear) {
            separators.push({ index: i, year: Number(currYear) });
        }
    }
    return separators;
};

// Clamps bezier control-point y values to the baseline so the smoothed curve never
// dips below the x-axis. A bézier stays within its convex hull, so clamping control
// points guarantees the rendered path stays within bounds without clipping the stroke.
export const clampPathToBaseline = (path: string, baselineY: number): string =>
    path.replace(
        /(-?\d+\.?\d*),(-?\d+\.?\d*)/g,
        (_match, x: string, y: string) => `${x},${Math.min(Number(y), baselineY)}`,
    );

// 8px bar in an 18px pitch (Figma spec) → bar is ~44% of its slot.
export const BAR_SLOT_FRACTION = 0.44;

export interface Point {
    x: number;
    y: number;
}

export interface BarRect {
    x: number;
    y: number;
    width: number;
    height: number;
    value: number;
    index: number;
}

// Semicircle-top bar path matching Figma's bar style; radius clamped to bar dimensions.
export const getBarPath = (bar: BarRect, radius: number = bar.width / 2): string => {
    const { x, y, width, height } = bar;
    const r = Math.max(0, Math.min(radius, width / 2, height));
    const right = x + width;
    const bottom = y + height;
    return [
        `M ${x} ${bottom}`,
        `L ${x} ${y + r}`,
        `Q ${x} ${y} ${x + r} ${y}`,
        `L ${right - r} ${y}`,
        `Q ${right} ${y} ${right} ${y + r}`,
        `L ${right} ${bottom}`,
        'Z',
    ].join(' ');
};

export const getBarRects = (
    data: MonthlyDatum[],
    plotWidth: number,
    plotHeight: number,
    scale: Scale,
): BarRect[] => {
    if (data.length === 0) return [];
    const slotWidth = plotWidth / data.length;
    const barWidth = Math.max(2, slotWidth * BAR_SLOT_FRACTION);

    return data.map((datum, index) => {
        const y = scale.getY(datum.totalActive);
        return {
            x: slotWidth * index + (slotWidth - barWidth) / 2,
            y,
            width: barWidth,
            height: plotHeight - y,
            value: datum.totalActive,
            index,
        };
    });
};

export const getLinePoints = (
    data: MonthlyDatum[],
    plotWidth: number,
    plotHeight: number,
    leftScale: Scale,
): { openingsPoints: Point[]; closuresPoints: Point[] } => {
    if (data.length === 0) return { openingsPoints: [], closuresPoints: [] };
    const slotWidth = plotWidth / data.length;

    return {
        openingsPoints: data.map((d, i) => ({ x: slotWidth * i + slotWidth / 2, y: leftScale.getY(d.openings) })),
        closuresPoints: data.map((d, i) => ({ x: slotWidth * i + slotWidth / 2, y: leftScale.getY(d.closures) })),
    };
};

export const getActiveIndexFromX = (x: number, plotWidth: number, dataLength: number): number => {
    if (dataLength === 0) return -1;
    const slotWidth = plotWidth / dataLength;
    return Math.max(0, Math.min(dataLength - 1, Math.floor(x / slotWidth)));
};

export interface YearLabel {
    year: string;
    x: number;
}

export const getYearLabels = (data: MonthlyDatum[], plotWidth: number): YearLabel[] => {
    if (data.length === 0) return [];
    const slotWidth = plotWidth / data.length;
    const labels: YearLabel[] = [];
    let currentYear = data[0].date.slice(0, 4);
    let yearStartIndex = 0;

    for (let i = 1; i <= data.length; i++) {
        const nextYear = i < data.length ? data[i].date.slice(0, 4) : null;
        if (nextYear !== currentYear || i === data.length) {
            labels.push({ year: currentYear, x: slotWidth * (yearStartIndex + (i - yearStartIndex) / 2) });
            if (nextYear) {
                currentYear = nextYear;
                yearStartIndex = i;
            }
        }
    }
    return labels;
};

export const formatLeftTick = (value: number): string => value.toLocaleString('en-US');

// Full locale (not "k") so auto-fitted small ranges don't produce duplicate labels.
export const formatRightTick = (value: number): string => value.toLocaleString('en-US');
